import React, { FC } from 'react';
import { Alert } from 'antd';
import { styled } from 'styled-components';
import { useTranslate } from '@/translations';
import { DamageLineView } from './damageLinePresentation';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 16px;
  border-bottom: 1px solid #f0f0f0;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 8px 0;
  border: 0;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? '#1677ff' : 'transparent')};
  background: transparent;
  color: ${({ $active }) => ($active ? '#1677ff' : '#595959')};
  font-weight: 600;
  cursor: pointer;
`;

const Footer = styled.div`
  margin-top: 8px;
  color: #8c8c8c;
`;

interface DamageLineReviewBarProps {
  view: DamageLineView;
  onViewChange: (view: DamageLineView) => void;
  total: number;
  removed: number;
  review: number;
  showRemovedBanner: boolean;
  showChangedBanner: boolean;
  showPtBanner: boolean;
  onDismiss: (key: 'removed' | 'changed' | 'pt') => void;
}

export const DamageLineReviewBar: FC<DamageLineReviewBarProps> = ({
  view,
  onViewChange,
  total,
  removed,
  review,
  showRemovedBanner,
  showChangedBanner,
  showPtBanner,
  onDismiss,
}) => {
  const { t } = useTranslate();
  const showTabs = review > 0;

  return (
    <Stack>
      {showRemovedBanner ? (
        <Alert
          type="info"
          showIcon
          closable
          message={t('removedAppraisalBanner', { count: removed })}
          description={t('removedAppraisalBody')}
          onClose={() => onDismiss('removed')}
        />
      ) : null}
      {showChangedBanner ? (
        <Alert
          type="info"
          showIcon
          closable
          message={t('resubmittedBanner')}
          description={t('resubmittedBody')}
          onClose={() => onDismiss('changed')}
        />
      ) : null}
      {showPtBanner ? (
        <Alert
          type="warning"
          showIcon
          closable
          message={t('ptReviewBanner')}
          description={t('ptReviewBannerBody')}
          onClose={() => onDismiss('pt')}
        />
      ) : null}
      {showTabs ? (
        <Tabs>
          <Tab
            type="button"
            $active={view === 'all'}
            onClick={() => onViewChange('all')}
          >
            {`${t('allDamage')} (${total})`}
          </Tab>
          <Tab
            type="button"
            $active={view === 'review'}
            onClick={() => onViewChange('review')}
          >
            {`${t('requiresReview')} (${review})`}
          </Tab>
          <Tab
            type="button"
            $active={view === 'completed'}
            onClick={() => onViewChange('completed')}
          >
            {`${t('completedLines')} (${total - review - removed})`}
          </Tab>
        </Tabs>
      ) : null}
    </Stack>
  );
};

export const DamageLineReviewFooter: FC<{
  total: number;
  removed: number;
  review: number;
}> = ({ total, removed, review }) => {
  const { t } = useTranslate();
  const extra =
    removed > 0
      ? t('removedAppraisalCount', { count: removed })
      : review > 0
        ? t('reviewItemsCount', { count: review })
        : '';

  return (
    <Footer>
      {t('damageItemsCount', { count: total })}
      {extra ? ` | ${extra}` : ''}
    </Footer>
  );
};

export const RemovedAppraisalBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border: 1px solid #ffccc7;
  border-radius: 999px;
  background: #fff;
  color: #cf1322;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;
